import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupIcon from '@mui/icons-material/Group';
import ScheduleIcon from '@mui/icons-material/Schedule';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import moment from 'moment';
import {
  Button,
  Datagrid,
  DateField,
  EditButton,
  FunctionField,
  List,
  ReferenceField,
  SelectField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useDataProvider,
  useGetRecordId,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import ListPagination from '../../components/ListPagination';
import Permission from '../../components/Permission';
import { DATE_FORMATER, daysOfTheWeek, PERMISSIONS } from '../../constants';

const frequencyLabels: Record<string, string> = {
  daily: 'Diária',
  weekly: 'Semanal',
};

const genderLabels: Record<string, string> = {
  F: 'Feminino',
  M: 'Masculino',
  X: 'Outro',
};

const dispatchStatusChoices = [
  { id: 'scheduled', name: 'Agendado' },
  { id: 'sending', name: 'Enviando' },
  { id: 'canceled', name: 'Cancelado' },
  { id: 'dispatched', name: 'Enviado' },
  { id: 'dispatched-with-error', name: 'Enviado com erro' },
  { id: 'failed', name: 'Falhou' },
];

const dispatchTypeChoices = [
  { id: 'sms', name: 'SMS' },
  { id: 'push-notification', name: 'Push' },
];

const genderChoices = [
  { id: 'F', name: 'Feminino' },
  { id: 'M', name: 'Masculino' },
  { id: 'X', name: 'Outro' },
];

const richTextSx = {
  '& img': { maxWidth: 300, maxHeight: 300 },
  '& p': { margin: 0 },
};

const ToggleActiveButton: React.FC = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const handleToggle = async () => {
    try {
      await dataProvider.update(`chains/chainId/recurring-send/${record.id}/toggle`, {
        id: '',
        data: { isActive: !record.isActive },
        previousData: record,
      });
      notify(record.isActive ? 'Envio pausado' : 'Envio retomado', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Erro ao alterar status', { type: 'error' });
    }
  };

  return (
    <Button
      label={record.isActive ? 'Pausar envio' : 'Retomar envio'}
      onClick={handleToggle}
      color={record.isActive ? 'warning' : 'success'}
    >
      {record.isActive ? <PauseIcon /> : <PlayArrowIcon />}
    </Button>
  );
};

const ShowActions: React.FC = () => (
  <TopToolbar>
    <ToggleActiveButton />
    <EditButton />
  </TopToolbar>
);

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Grid size={12}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} component="div">
      {children}
    </Typography>
  </Grid>
);

const BasicInfoBox: React.FC = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <InfoIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>Informações Básicas</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <FieldRow label="Título">{record.title || '—'}</FieldRow>
          <FieldRow label="Status">
            <Tooltip
              arrow
              placement="right"
              title={
                record.isActive
                  ? 'Envio ativo — novos disparos serão realizados automaticamente conforme o agendamento configurado.'
                  : 'Envio pausado — nenhum novo disparo será gerado enquanto estiver nesse estado. Disparos já agendados são cancelados ao pausar.'
              }
            >
              <Chip
                icon={<InfoOutlinedIcon />}
                label={record.isActive ? 'Ativo' : 'Pausado'}
                size="small"
                color={record.isActive ? 'success' : 'default'}
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          </FieldRow>
          {record.createdAt && (
            <FieldRow label="Criado em">
              {moment(record.createdAt).format('DD/MM/YYYY HH:mm')}
            </FieldRow>
          )}
        </Grid>
      </Paper>
    </Grid>
  );
};

const SegmentationBox: React.FC = () => {
  const record = useRecordContext();
  if (!record) return null;

  const hasSegmentation = !!(record.customerTagId || record.stationTagId || record.gender);

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <GroupIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>Segmentação</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {!hasSegmentation ? (
          <Typography variant="body2" color="text.secondary">
            Sem segmentação — todos os clientes da rede com app instalado serão alcançados.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {record.customerTagId && (
              <FieldRow label="Etiqueta de cliente">
                <ReferenceField
                  record={record}
                  source="customerTagId"
                  reference="chains/chainId/tag"
                  link={false}
                >
                  <FunctionField
                    render={(tag: any) => (
                      <Chip label={tag?.name || '—'} size="small" color="secondary" />
                    )}
                  />
                </ReferenceField>
              </FieldRow>
            )}
            {record.stationTagId && (
              <FieldRow label="Etiqueta de posto">
                <ReferenceField
                  record={record}
                  source="stationTagId"
                  reference="chains/chainId/tag"
                  link={false}
                >
                  <FunctionField
                    render={(tag: any) => (
                      <Chip label={tag?.name || '—'} size="small" color="primary" />
                    )}
                  />
                </ReferenceField>
              </FieldRow>
            )}
            {record.gender && (
              <FieldRow label="Sexo do cliente">
                <Chip label={genderLabels[record.gender] || record.gender} size="small" />
              </FieldRow>
            )}
          </Grid>
        )}
      </Paper>
    </Grid>
  );
};

const ScheduleBox: React.FC = () => {
  const record = useRecordContext();
  if (!record) return null;

  const isWeekly = record.frequency === 'weekly';
  const schedules: any[] = record.dayScheduledRecurringSends || [];

  return (
    <Grid size={{ xs: 12, md: isWeekly ? 12 : 6 }}>
      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <ScheduleIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>Agendamento</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Frequência
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {frequencyLabels[record.frequency] || '—'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Data inicial
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {record.startAt ? moment(record.startAt).format('DD/MM/YYYY HH:mm') : '—'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Data final
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {record.endAt ? moment(record.endAt).format('DD/MM/YYYY HH:mm') : '—'}
            </Typography>
          </Grid>
        </Grid>

        {!isWeekly && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary" display="block">
              Horário de envio
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {record.startTime || '—'}
            </Typography>
          </Box>
        )}

        {isWeekly && (
          <Box mt={2}>
            <Typography variant="subtitle2" mb={1}>Horários Semanais</Typography>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Dia</strong></TableCell>
                    <TableCell align="center"><strong>Horário</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {daysOfTheWeek.map((day) => {
                    const schedule = schedules.find((s: any) => s.dayOfTheWeek === day.id);
                    const isActive = !!(schedule && schedule.isActive && schedule.startTime);
                    return (
                      <TableRow key={day.id}>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{day.name}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                          {isActive ? schedule.startTime : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {isActive ? (
                            <Chip label="Ativo" size="small" color="success" />
                          ) : (
                            <Chip label="Inativo" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Grid>
  );
};

const MessagesBox: React.FC = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Grid size={{ xs: 12 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <NotificationsIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>Canais e Mensagens</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
          <Chip
            label="Push"
            size="small"
            color={record.pushNotification ? 'primary' : 'default'}
            variant={record.pushNotification ? 'filled' : 'outlined'}
          />
          <Chip
            label="SMS"
            size="small"
            color={record.sms ? 'primary' : 'default'}
            variant={record.sms ? 'filled' : 'outlined'}
          />
        </Stack>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Descrição (exibida no app)
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: '#fafafa',
                ...richTextSx,
              }}
              dangerouslySetInnerHTML={{ __html: record.body || '<em>—</em>' }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box mb={2}>
              <Typography variant="caption" color="text.secondary" display="block">
                Mensagem Push
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {record.pushMessage || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Mensagem SMS
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {record.smsMessage || '—'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

const ShowRecurringSend: React.FC = (props) => {
  const id = useGetRecordId();

  return (
    <Permission permission={PERMISSIONS.UNIFIED_SEND}>
      <Show title="Envio Recorrente" actions={<ShowActions />} {...props}>
        <TabbedShowLayout>
          <Tab label="Informações">
            <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
              <Grid container spacing={2}>
                <BasicInfoBox />
                <SegmentationBox />
                <ScheduleBox />
                <MessagesBox />
              </Grid>
            </Box>
          </Tab>

          <Tab label="Clientes que receberão">
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Clientes da rede que se encaixam na segmentação configurada e possuem o app instalado
              (token de notificação válido). Na etiqueta de posto, entram os clientes cujo primeiro
              abastecimento foi em um posto da etiqueta — o mesmo critério do relatório "Primeiro
              Abastecimento por Posto", com a diferença de que aqui só aparece quem tem o app.
              Esta é uma prévia atualizada — a lista real pode variar a cada disparo conforme
              novos cadastros e tokens.
            </Typography>
            <List
              title=" "
              resource={`chains/chainId/recurring-send/${id}/matching-customers`}
              sort={{ field: 'name', order: 'ASC' }}
              perPage={25}
              pagination={<ListPagination />}
              exporter={false}
              actions={false}
              empty={false}
              disableSyncWithLocation
            >
              <Datagrid bulkActionButtons={false} rowClick={false}>
                <TextField source="name" label="Nome" emptyText="—" />
                <TextField source="cellphone" label="Celular" emptyText="—" sortable={false} />
                <TextField source="email" label="E-mail" emptyText="—" sortable={false} />
                <SelectField source="gender" label="Sexo" choices={genderChoices} emptyText="—" sortable={false} />
              </Datagrid>
            </List>
          </Tab>

          <Tab label="Disparos realizados">
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Histórico de disparos gerados por este envio recorrente — cada execução agendada
              cria um registro por canal (Push e/ou SMS) com o status atual e contagens de envio.
            </Typography>
            <List
              title=" "
              resource={`chains/chainId/recurring-send/${id}/dispatch-messages`}
              sort={{ field: 'sendAt', order: 'DESC' }}
              perPage={25}
              pagination={<ListPagination />}
              exporter={false}
              actions={false}
              empty={false}
              disableSyncWithLocation
            >
              <Datagrid bulkActionButtons={false} rowClick={false}>
                <DateField source="sendAt" label="Data do envio" options={DATE_FORMATER} showTime />
                <SelectField source="type" label="Canal" choices={dispatchTypeChoices} />
                <SelectField source="status" label="Status" choices={dispatchStatusChoices} />
                <FunctionField
                  label="Total"
                  textAlign="center"
                  render={(r: any) => r?.totalDispatched || r?.cardinality || 0}
                />
                <FunctionField
                  label="Recebidos"
                  textAlign="center"
                  render={(r: any) => r?.totalReceived || 0}
                />
                <FunctionField
                  label="Lidos"
                  textAlign="center"
                  render={(r: any) => r?.totalRead || 0}
                />
                <FunctionField
                  label="Cliques"
                  textAlign="center"
                  render={(r: any) => r?.totalClicked || 0}
                />
                <FunctionField
                  label="Erros"
                  textAlign="center"
                  render={(r: any) => r?.totalErrors || 0}
                />
              </Datagrid>
            </List>
          </Tab>
        </TabbedShowLayout>
      </Show>
    </Permission>
  );
};

export default ShowRecurringSend;
